import React from "react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Plus,
  CheckCircle2,
  Banknote,
  Receipt,
  ShieldCheck,
  XCircle,
} from "lucide-react";

function fmtDT(s) {
  if (!s) return null;
  try {
    return new Date(s).toLocaleString(undefined, { dateStyle: "medium", timeStyle: "short" });
  } catch (_) {
    return s;
  }
}
function fmtINR(n) {
  if (n == null || isNaN(n)) return "—";
  return new Intl.NumberFormat("en-IN", { style: "currency", currency: "INR", maximumFractionDigits: 2 }).format(n);
}

/**
 * Timeline of life-cycle events for an OPA: created → approved/rejected →
 * payment released → reimbursement submitted → settled. Each step renders
 * only when its underlying field exists on the OPA doc.
 *
 * Props: { opa: doc | null, onClose: () => void }
 */
export default function OPATimelineDialog({ opa, onClose }) {
  if (!opa) return null;

  const events = [];

  events.push({
    icon: Plus,
    color: "text-zinc-300",
    ring: "ring-zinc-500/30 bg-zinc-500/10",
    label: "OPA Created",
    when: opa.created_at,
    by: opa.requested_by_name,
    note: opa.amount != null ? `Requested ${fmtINR(opa.amount)}` : null,
  });

  if (opa.status === "rejected") {
    events.push({
      icon: XCircle,
      color: "text-rose-300",
      ring: "ring-rose-500/30 bg-rose-500/10",
      label: "Rejected",
      when: opa.rejected_at || opa.approved_at,
      by: opa.rejected_by_name || opa.approved_by_name,
      note: opa.rejection_reason || null,
    });
  } else if (opa.approved_at || opa.status === "approved") {
    events.push({
      icon: CheckCircle2,
      color: "text-sky-300",
      ring: "ring-sky-500/30 bg-sky-500/10",
      label: "Approved",
      when: opa.approved_at,
      by: opa.approved_by_name,
      note: `Approved Amount ${fmtINR(opa.amount)}`,
    });
  }

  if (opa.released_at) {
    events.push({
      icon: Banknote,
      color: "text-emerald-300",
      ring: "ring-emerald-500/30 bg-emerald-500/10",
      label: "Payment Released",
      when: opa.released_at,
      by: opa.released_by_name,
      note: opa.transaction_id ? `Txn ID ${opa.transaction_id}` : null,
    });
  }

  if (opa.reimbursement) {
    events.push({
      icon: Receipt,
      color: "text-violet-300",
      ring: "ring-violet-500/30 bg-violet-500/10",
      label: "Reimbursement Submitted",
      when: opa.reimbursement.submitted_at,
      by: opa.reimbursement.submitted_by_name,
      note: `Form ${opa.reimbursement.form_number} · ${fmtINR(opa.reimbursement.total_value)}`,
    });
  }

  if (opa.settlement) {
    const d = opa.settlement;
    const dirText = d.direction === "pay"
      ? `Company paid user ${fmtINR(d.delta)}`
      : `User returned ${fmtINR(d.delta)}`;
    events.push({
      icon: ShieldCheck,
      color: "text-cyan-300",
      ring: "ring-cyan-500/30 bg-cyan-500/10",
      label: "Settled",
      when: d.settled_at,
      by: d.settled_by_name,
      note: `${dirText}${d.transaction_id ? ` · Txn ${d.transaction_id}` : ""}`,
    });
  }

  return (
    <Dialog open={!!opa} onOpenChange={(o) => !o && onClose()}>
      <DialogContent
        className="bg-zinc-950 border-white/10 text-zinc-100 sm:max-w-md"
        data-testid="opa-timeline-dialog"
      >
        <DialogHeader>
          <DialogTitle className="text-white flex items-center gap-2" style={{ fontFamily: "Outfit" }}>
            <span className="font-mono text-emerald-400">{opa.code}</span> Timeline
          </DialogTitle>
          <DialogDescription className="text-zinc-500">
            {opa.purpose || "OPA lifecycle events"}
          </DialogDescription>
        </DialogHeader>
        <ol className="mt-4 relative border-l border-white/10 pl-6 space-y-5">
          {events.slice().reverse().map((e, idx) => {
            const Icon = e.icon;
            return (
              <li key={idx} data-testid={`opa-timeline-event-${idx}`}>
                <span className={`absolute -left-3 flex items-center justify-center w-6 h-6 rounded-full ring-2 ${e.ring}`}>
                  <Icon className={`w-3.5 h-3.5 ${e.color}`} />
                </span>
                <div className="flex items-baseline justify-between gap-3">
                  <div className={`text-sm font-medium ${e.color}`}>{e.label}</div>
                  <div className="text-[11px] text-zinc-500 font-mono">{fmtDT(e.when) || "—"}</div>
                </div>
                {e.by && <div className="text-[11px] text-zinc-400 mt-0.5">by <span className="text-zinc-200">{e.by}</span></div>}
                {e.note && <div className="text-xs text-zinc-300 mt-1">{e.note}</div>}
              </li>
            );
          })}
        </ol>
      </DialogContent>
    </Dialog>
  );
}
